/**
 * Interpreting a refused `GET esign/start-esign`.
 *
 * cpcore answers a refused start with HTTP 200 and `{ success: false, message: <reason>, data: {} }`
 * (esign.service.ts `startEsign`: the exposure re-check that just moved the journey back to the
 * bank-statement flow, or the step gates on a credit-path journey). `fetchData` (src/lib/helper.js)
 * returns that parsed body as-is, so the caller has to look at `success` itself — a plain
 * `await fetchData(...)` followed by `navigate(esignUrl)` would send the tenant to the eSign
 * screen on a journey that no longer allows it.
 *
 * Reason codes are the server's, the copy is ours. Anything unknown gets the generic line rather
 * than a raw code on screen.
 *
 * Lives under src/b2b/ on purpose (see journeyGates.js): B2B and D2C share no code.
 */

export const ESIGN_START_REFUSAL_MESSAGES = Object.freeze({
  BANKING_REQUIRED: "Please complete the bank check first.",
  PENNY_DROP_REQUIRED: "Please verify your bank account first.",
  // The journey's lender signs only on the no-bank-statement path and this journey is not on it
  // (cpcore esign.service.ts `startEsign`, owner ruling L14). Nothing the tenant can retry fixes it.
  // Same copy as src/lib/esignStartDigio.js START_DIGIO_REFUSAL_MESSAGES.
  LENDER_REQUIRES_WITHOUT_BANKING:
    "We can't continue to signing right now. Please contact our support team and they will help you with the next step.",
});

export const ESIGN_START_REFUSAL_FALLBACK =
  "We could not start the e-sign. Please refresh and try again.";

/** True only for the explicit refusal shape; `undefined` (a swallowed non-2xx) is not one. */
export function isEsignStartRefused(res) {
  return Boolean(res) && typeof res === "object" && res.success === false;
}

/** Plain-English copy for a server reason code. */
export function esignStartRefusalMessage(code) {
  if (
    typeof code === "string" &&
    Object.prototype.hasOwnProperty.call(ESIGN_START_REFUSAL_MESSAGES, code)
  ) {
    return ESIGN_START_REFUSAL_MESSAGES[code];
  }
  return ESIGN_START_REFUSAL_FALLBACK;
}
